import React from 'react';
import IndustryPageTemplate from '../../components/IndustryPageTemplate';
import SEO from '../../components/SEO';

const AutoDetailing = () => {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "How can Local Business Heroes help my auto detailing business?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Local Business Heroes helps auto detailing businesses capture more leads, automate follow-ups, reduce no-shows, and grow faster. Rated 4.9/5 by 1,200+ UK businesses."
        }
      },
      {
        "@type": "Question",
        "name": "How much does it cost for auto detailing businesses?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Plans start at £297/month with a 14-day trial for just £5. Most auto detailing businesses see ROI within the first month."
        }
      },
      {
        "@type": "Question",
        "name": "Do I need technical skills to use it?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "No. Local Business Heroes is designed for busy business owners with zero tech experience. Setup takes 5 minutes with UK-based support."
        }
      },
      {
        "@type": "Question",
        "name": "Will it work for my auto detailing business?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes. Local Business Heroes is specifically designed for local service businesses like auto detailing. Features include appointment scheduling, automated reminders, lead capture, and customer management."
        }
      }
    ]
  };

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "CRM for Auto Detailing - Local Business Heroes",
    "applicationCategory": "BusinessApplication",
    "offers": {
      "@type": "Offer",
      "price": "297",
      "priceCurrency": "GBP"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": "1200"
    }
  };

  const breadcrumbs = [
    { name: "Home", url: "/" },
    { name: "Industries", url: "/industries" },
    { name: "Auto Detailing CRM", url: "/industries/auto-detailing" }
  ];

  const industryData = {
    name: 'Auto Detailing',
    icon: '🚗',
    title: 'Fill Your Detailing Schedule & Build Recurring Customers',
    subtitle: 'Online booking, before/after photo automation, and maintenance reminders that keep customers coming back',
    
    challenges: [
      'Seasonal business with slow periods in winter months',
      'Customers only detail their car once and never return',
      'Spending hours on the phone booking appointments and giving quotes',
      'Weather delays causing scheduling chaos and customer frustration',
      'Hard to showcase your work quality to attract new customers',
      'Competing with cheap car washes instead of premium detailing',
      'Mobile detailing logistics make communication difficult',
      'Not enough reviews to stand out from competitors'
    ],
    
    solutions: [
      'Online booking lets customers schedule detailing 24/7 with instant confirmation',
      'Seasonal maintenance reminders bring customers back every 3-6 months automatically',
      'Instant quote calculator for standard packages speeds up bookings',
      'Before/after photo automation showcases your work and generates referrals',
      'Package promotions encourage customers to commit to regular maintenance plans',
      'Weather update SMS keeps customers informed without manual calls',
      'Review automation after each detail builds your reputation online',
      'Missed call text-back captures enquiries even when you\'re detailing cars'
    ],
    
    features: [
      '24/7 Online Booking with Package Selection',
      'Instant Quote Calculator for Standard Services',
      'Automated Appointment Reminders',
      'Before/After Photo Collection & Showcase',
      'Seasonal Maintenance Reminder Campaigns (3/6/12 months)',
      'Package Promotion & Upsell Automation',
      'Weather Delay SMS Notifications',
      'Missed Call Text-Back System',
      'Google Review Request After Service',
      'Customer Vehicle History Tracking',
      'Automated Invoicing & Payment',
      'Referral Incentive Campaigns'
    ],
    
    results: [
      '55% of customers on maintenance plans',
      'Fill schedule 2 weeks in advance',
      '70% increase in winter bookings',
      'Showcase 500+ before/after photos',
      'Average 4.9★ Google rating',
      'Reduce admin time by 10 hours/week'
    ],
    
    reviews: [
      {
        author: 'Tom Henderson',
        location: 'Manchester',
        text: 'The seasonal reminders have completely changed my business. Customers now automatically rebook every 3-6 months instead of detailing once and disappearing. 55% of my customers are on regular maintenance plans. Consistent income year-round.',
        result: '55% customers on maintenance plans'
      },
      {
        author: 'Mark Davies',
        location: 'Birmingham',
        text: 'The before/after photo automation has been incredible for marketing. The system automatically requests photos and showcases them. New customers constantly mention seeing the photos. My bookings have increased 60% without spending on ads.',
        result: '60% increase in bookings from photos'
      },
      {
        author: 'James Wilson',
        location: 'Leeds',
        text: 'Online booking has saved me 8 hours per week on phone calls. Customers can see my availability and book instantly. 70% of my bookings now come through online. I can focus on detailing instead of being glued to my phone.',
        result: 'Saves 8 hours weekly, 70% online bookings'
      },
      {
        author: 'Paul Roberts',
        location: 'Bristol',
        text: 'The instant quote calculator has doubled my conversion rate. Customers get pricing immediately for ceramic coating, interior detailing, or full packages. I\'m not losing leads to competitors who respond faster.',
        result: 'Quote conversion rate doubled'
      },
      {
        author: 'Steve Mitchell',
        location: 'Liverpool',
        text: 'The review automation got me from 11 reviews to 118 in 9 months. I\'m now the top-rated auto detailing service in Liverpool. The reviews mention my attention to detail and the photos, which brings in higher-paying customers.',
        result: '118 reviews, top-rated in area'
      },
      {
        author: 'David Foster',
        location: 'Glasgow',
        text: 'The maintenance plans have stabilized my income. Instead of feast-or-famine, I have 85 customers on quarterly plans. That\'s predictable revenue and I can plan my schedule properly. Winter is no longer slow because customers are locked in.',
        result: '85 customers on quarterly plans'
      }
    ]
  };

  return (
    <>
      <SEO 
        title="CRM for Auto Detailing UK | Marketing Automation | Local Business Heroes"
        description="Rated 4.9/5. CRM and marketing automation for auto detailing businesses. Capture more leads, automate follow-up, grow faster. Start £5 trial today."
        keywords="CRM for auto detailing UK, auto detailing business software, auto detailing customer management, auto detailing lead generation UK"
        url="/industries/auto-detailing"
        schemaMarkup={[faqSchema, localBusinessSchema]}
        breadcrumbs={breadcrumbs}
      />
      <IndustryPageTemplate industry={industryData} />
    </>
  );
};

export default AutoDetailing;